import React, { useCallback, useEffect, useState, useRef } from 'react'
import { LatLngBounds, LatLngExpression, Map as LeafletMap } from 'leaflet'
import dayjs from 'dayjs'
import Box from '@mui/material/Box'
import Collapse from '@mui/material/Collapse'
import Container from '@mui/material/Container'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Divider from '@mui/material/Divider'
import Tab from '@mui/material/Tab'
import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'
import ToggleButton from '@mui/material/ToggleButton'
import Typography from '@mui/material/Typography'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import ErrorBox from '@/components/shared/boxes/ErrorBox'
import {
  DeleteRoute,
  LoadAccountMemberList, LoadFormList, LoadProjectList, LoadResourcePlaceList, LoadRouteList, LoadUserLocationList,
  LoadWorkFinishOptionList, LoadWorkLocationList
} from '@/javascript/domain/usecases'
import HeaderBox from '@/javascript/presentation/components/boxes/header-box'
import DateInput from '@/javascript/presentation/components/inputs/date-input'
import { getBoundsFromPoints } from '@/javascript/presentation/helpers'
import { MapBox, EmptyBox, Filters } from './components'
import { RouteItem } from '@/javascript/domain/entities/maps/route-item'
import GreenBox from '@/components/shared/boxes/GreenBox'
import TourModal from './components/tour-modal/TourModal'
import RoutesBox from './components/routes-box/routes-box'
import './work-user-maps-styles.scss'
import Paywall from './components/Paywall'
import { UserLocation } from '@/javascript/domain/entities/maps/user-location'
import { WorkLocation } from '@/javascript/domain/entities/maps/work-location'

export type FetchLocationsParams = {
  date?: Date
  viewMode?: string[]
  searchText?: string
  resourcePlaces?: string[]
  forms?: string[]
  projects?: string[]
  providers?: string[]
  inspectors?: string[]
  status?: string
  finishOptions?: string[]
}

type Props = {
  loadAccountMemberList: LoadAccountMemberList
  loadFormList: LoadFormList
  loadProjectList: LoadProjectList
  loadResourcePlaces: LoadResourcePlaceList
  loadUserLocationList: LoadUserLocationList
  loadWorkFinishOptionList: LoadWorkFinishOptionList
  loadWorkLocationList: LoadWorkLocationList
  loadRouteList: LoadRouteList
  deleteRoute: DeleteRoute
  canCreateRoute: boolean
  isWorkTeamRoutingEnabled: boolean
  pollInterval?: number
}

const WorkUserMaps: React.FC<Props> = ({
  loadAccountMemberList, loadFormList, loadProjectList, loadResourcePlaces, loadUserLocationList,
  loadWorkFinishOptionList, loadWorkLocationList, loadRouteList, deleteRoute, canCreateRoute, isWorkTeamRoutingEnabled, pollInterval = 30000
}) => {
  const [locations, setLocations] = useState({
    works: [] as WorkLocation[],
    users: [] as UserLocation[],
    center: [0.0, 0.0] as LatLngExpression,
    bounds: null as LatLngBounds | null
  })
  const [isReady, setIsReady] = useState(false)
  const [isError, setIsError] = useState(false)
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    const tab = params.get('tab')
    return tab !== null ? parseInt(tab) : 0
  })
  const [date, setDate] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    const dateParam = params.get('date')
    return dateParam ? dayjs(dateParam) : dayjs()
  })
  const [routes, setRoutes] = useState<RouteItem[]>([])
  const [isRoutesReady, setIsRoutesReady] = useState(false)
  const [toggleFilter, setToggleFilter] = useState(false)
  const [routeBounds, setRouteBounds] = useState<LatLngBounds | null>(null)
  const lastLoadedRouteDate = useRef<string | null>(null)
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const lastFetchParamsRef = useRef<FetchLocationsParams>({})
  const hasInitiallyFitRef = useRef(false)

  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }
  }

  const urlParams = new URLSearchParams(window.location.search) 
  const isDemo = urlParams.get('demo') === 'true'
  const [isDemoError, setIsDemoError] = useState(false)

  const handleTab = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
  }

  const mapRef = useRef<LeafletMap>(null)
  const routeMapRef = useRef<LeafletMap>(null)

  const isMobileDevice = () => {
    if (typeof window === 'undefined') return false

    const userAgent = window.navigator.userAgent.toLowerCase()
    
    const mobileKeywords = [
      'android', 'webos', 'iphone', 'ipad', 'ipod', 
      'blackberry', 'windows phone', 'mobi', 'tablet'
    ]
    const isMobileUA = mobileKeywords.some(keyword => userAgent.includes(keyword))
    const isSmallScreen = window.innerWidth <= 768;

    return isMobileUA || isSmallScreen
  }

  const isMobile = isMobileDevice();
  
  const [showTourModal, setshowTourModal] = useState(false)

  const checkShouldShowModalTourToday = () => {
    const today = new Date().toISOString().split('T')[0]
    const lastSeen = localStorage?.getItem('tour_last_seen')
    const isCompleted = localStorage?.getItem('tour_completed')
    const userTourCompleted = localStorage?.getItem('map_tour_completed')

    if (isCompleted === 'true' || userTourCompleted === 'true' ) return false

    if (!lastSeen) return false

    return lastSeen !== today
  }

  const handleRemindLater = () => {
    const today = new Date().toISOString().split('T')[0]
    localStorage?.setItem('tour_last_seen', today)

    setshowTourModal(false)
    window.location.href = '/work_team_maps'
  }

  const handleFinalizeTour = () => {
    localStorage?.setItem('tour_completed', 'true')
    setshowTourModal(false)
    window.location.href = '/work_team_maps'
  }

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const isURLTour = urlParams.get('demo') === 'true'

    if (isURLTour && typeof window.Intercom === 'function') {
      stopPolling()
      setTimeout(() => {
        window.Intercom('startTour', 662855)
      }, 100)
    } else {
      window.Intercom('cancelTour')
      window.Intercom('update', { last_request_at: Math.floor(Date.now() / 1000) })
    }

  }, [])

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const isDemoMode = urlParams.get('demo') === 'true'
    const lastSeen = localStorage?.getItem('tour_last_seen')
    const userTourCompleted = localStorage?.getItem('map_tour_completed')

    if (isDemoMode) return 

    if (locations.users.length > 0 && lastSeen && !userTourCompleted) {
      if (typeof window.Intercom === 'function') {
        

        setTimeout(() => {
          window.Intercom('startTour', 668894)
          localStorage?.setItem('map_tour_completed', 'true')
        }, 1000) 
      }
    }
  }, [locations.users]) 

  useEffect(() => {
    const isMobile = isMobileDevice();
    const urlParams = new URLSearchParams(window.location.search)
    const isDemoMode = urlParams.get('demo') === 'true'

    if (isMobile && isDemoMode) {
      
      setshowTourModal(true);
    }
  }, []);

  
  useEffect(() => {
    let tourWasVisible = false
    let invisibilityCounter = 0;

    if (checkShouldShowModalTourToday()) {
      setshowTourModal(true);
    }

    const handleTourEnd = () => {
      const today = new Date().toISOString().split('T')[0];
      setshowTourModal(true)
      localStorage?.setItem('tour_last_seen', today)
    }

    const monitor = setInterval(() => {
      const tourElement = document.querySelector('.intercom-tour-frame, .intercom-messenger-frame, #intercom-container')

      const isVisible = !!(
        tourElement && 
        tourElement.offsetHeight > 0 &&
        window.getComputedStyle(tourElement).display !== 'none' &&
        window.getComputedStyle(tourElement).visibility !== 'hidden'
      )

      if (isVisible) {
        tourWasVisible = true
        invisibilityCounter = 0
      } else if (tourWasVisible) {
        invisibilityCounter += 500
      }

      const isURLTour = urlParams.get('demo') === 'true'
      if (tourWasVisible && isURLTour && invisibilityCounter >= 800) {
        stopPolling()
        tourWasVisible = false
        handleTourEnd() 
        clearInterval(monitor)
      }
    }, 500)

  return () => clearInterval(monitor)
  }, [])

  useEffect(() => {
    if (!isReady) return;

    const allLocations = [...locations.works, ...locations.users]
    if (allLocations.length > 0) {
      const points = allLocations.map(l => [parseFloat(l.latitude), parseFloat(l.longitude)] as [number, number])
      const bounds = getBoundsFromPoints(points);
      if (bounds) {
        setLocations(old => ({ ...old, bounds, center: bounds.getCenter() }))
      }
    } 
    else if (isDemoError) {
      setLocations(old => ({ 
        ...old, 
        bounds: null, 
        center: [-14.235, -51.925] as LatLngExpression 
      }))
    }
  }, [locations.users, locations.works, isDemoError, isReady])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!mapRef.current) return

      const map = mapRef.current
      
      if (locations.bounds) {
        map.fitBounds(locations.bounds)
      } else {
        const defaultCenter: LatLngExpression = [-14.235, -51.925]
        map.setView(locations.center || defaultCenter, 4)
      }
    }, 100)
    return () => clearTimeout(timer);
  }, [locations.bounds])

  useEffect(() => {
    if (activeTab !== 1) return
    const dateKey = date.format('YYYY-MM-DD')
    if (dateKey === lastLoadedRouteDate.current) return
    lastLoadedRouteDate.current = dateKey
    setIsRoutesReady(false)
    loadRouteList.load({ date: date.toDate() }).then(setRoutes).finally(() => setIsRoutesReady(true))
  }, [activeTab, date])

  useEffect(() => {
    const members = routes.flatMap(r => r.members)
    if (members.length === 0) return
    const points = members.flatMap(m => m.points.map(p => [p.location.lat, p.location.lng] as [number, number]))
    setRouteBounds(getBoundsFromPoints(points))
  }, [routes])

  useEffect(() => {
    if (routeMapRef.current && routeBounds) routeMapRef.current.fitBounds(routeBounds, { padding: [40, 40] })
  }, [routeBounds])


  const loadLocations = useCallback(async (params: FetchLocationsParams): Promise<[UserLocation[], WorkLocation[]]> => {
    const recentDate = dayjs(params?.date).isSame(dayjs(), 'date')
    const resolveUsers = (!params?.viewMode || params.viewMode.includes("users")) && recentDate
    const resolveWorks = !params?.viewMode || params.viewMode.includes("works")
    return Promise.all([
      resolveUsers ? loadUserLocationList.load(params) : Promise.resolve([]),
      resolveWorks ? loadWorkLocationList.load(params) : Promise.resolve([])
    ])
  }, [loadUserLocationList, loadWorkLocationList])

  const fetchLocations = async (params?: FetchLocationsParams) => {
        const urlParams = new URLSearchParams(window.location.search)
    const isDemo = urlParams.get('demo') === 'true'

    if (isDemo){
      try{
        setIsReady(false)
        setIsDemoError(false)
        
        const today = dayjs().format('YYYY-MM-DD HH:mm') 

        const mockWorks: LoadWorkLocationList.Model[] = [
          {
            id: 1,
            description: '[Exemplo] Relatório Fotográfico de Serviço - Edifício Dallas',
            status: 'finished',
            startTime: today,
            latitude:'-25.430583',
            longitude:'-49.273167', 
            accountId: 0 ,
            hierarchyName:'Local de Exemplo',
            resourcePlaceId:1,
            resourcePlaceName: 'teste',
            isLate: false,
          },
          {
            id: 2,
            description: '[Exemplo] Relatório Fotográfico de Serviço - Edifício Dallas',
            status: 'reviewed',
            startTime: today,
            latitude:'-25.433500',
            longitude:'-49.272194', 
            accountId: 0 ,
            hierarchyName:'Local de Exemplo',
            resourcePlaceId:1,
            resourcePlaceName: 'teste',
            isLate: false,
          },
          {
            id: 3,
            description: '[Exemplo] Relatório Fotográfico de Serviço - Edifício Dallas',
            status: 'not_started',
            startTime: today,
            latitude:'-25.433583',
            longitude:'-49.275944', 
            accountId: 0 ,
            hierarchyName:'Local de Exemplo',
            resourcePlaceId:1,
            resourcePlaceName: 'teste',
            isLate: false,
          },
          {
            id: 4,
            description: '[Exemplo] Relatório Fotográfico de Serviço - Edifício Dallas',
            status: 'started',
            startTime: today,
            latitude:'-25.430861',
            longitude:'-49.278028', 
            accountId: 0 ,
            hierarchyName:'Local de Exemplo',
            resourcePlaceId:1,
            resourcePlaceName: 'teste',
            isLate: false,
          },
        ]
        const mockUsers: LoadUserLocationList.Model[] = [
          {
            id: 1,
            name: 'Anderson Pereira [Exemplo]',
            email: 'andersonpereira@exemplo.com',
            latitude: '-25.430556',
            longitude: '-49.269000',
            accuracy: 1,
            lastSeenAt: today,
            description: '[Exemplo] Relatório Fotográfico de Serviço - Edifício Dallas',
          }
        ]

        setLocations({...locations, works: mockWorks,users: mockUsers})
      } catch (error) {
        setLocations(old => ({ ...old, works: [], users: [] }))
        setIsDemoError(true)
        
      } finally {
        setIsReady(true)
      }
      return
    }
    lastFetchParamsRef.current = params ?? {}
    hasInitiallyFitRef.current = false
    setIsReady(false)
    setIsError(false)
    const [users, works] = await loadLocations(params ?? {})
      .catch((): [UserLocation[], WorkLocation[]] => {
        setIsError(true)
        return [[], []]
      })
    setIsReady(true)
    setLocations(prev => ({ ...prev, works, users }))
  }

  const refreshLocations = useCallback(async () => {
    const [users, works] = await loadLocations(lastFetchParamsRef.current)
      .catch((): [UserLocation[], WorkLocation[]] => [[], []])
    setLocations(prev => ({ ...prev, works, users }))
  }, [loadLocations])

  useEffect(() => {
    stopPolling()
    const isToday = date.isSame(dayjs(), 'date')
    if (activeTab === 0 && isToday && !isDemo) {
      pollIntervalRef.current = setInterval(refreshLocations, pollInterval)
    }
    return () => stopPolling()
  }, [activeTab, date, refreshLocations])

  return (
    <TabContext value={activeTab}>
      <TourModal 
        open={showTourModal} 
        onClose={handleFinalizeTour} 
        remindLater={handleRemindLater}
        isMobile={isMobile}
      />
      <Box sx={{ bgcolor: 'background.header' }}>
        <HeaderBox sx={{ paddingBottom: toggleFilter ? '1.5rem' : 0 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant='h3'>Mapa</Typography>
            <Box>
              <Chip label='Beta' size='medium' sx={{ fontSize: '14px' }} />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', alignSelf: 'stretch' }}>
            <DateInput value={date} setValue={setDate} />
            {activeTab == 0 &&
              <ToggleButton
                value="check"
                sx={{ border: 'none', borderRadius: '50%' }}
                selected={toggleFilter}
                onChange={() => setToggleFilter(!toggleFilter)}
                data-testid="filter-toggle"
              >
                <FilterAltIcon />
              </ToggleButton>
            }
          </Box>
          {isDemoError &&
            <Container disableGutters sx={{padding:0}}>
              <ErrorBox
                errorMessage="Não se preocupe, mesmo assim você pode utilizar todas as funcionalidades do sistema."
                onClose={() => setIsError(false)} 
                title="Algo deu errado na personalização da sua conta. "
              />
            </Container>
          }
          {isDemo && !isDemoError &&
            <Container disableGutters sx={{padding:0}}>
              <GreenBox
                title="Aqui está uma simulação de como as suas futuras atividades aparecem no Mapa quando possuem endereço vinculado. 🎉"
              > </GreenBox>
            </Container>
          }
          <Box>
            <TabList onChange={handleTab} centered>
              <Tab label="Atividades e equipe" sx={{ minWidth: '15vw'}} value={0} />
              <Tab label="Rotas" sx={{ minWidth: '15vw'}} value={1} data-testid="route-tab" />
            </TabList>
            <Divider />
          </Box>
          <Collapse in={toggleFilter} data-testid="filter-collapse">
            <TabPanel value={0} sx={{ padding: 0 }} keepMounted>
              <Filters
                date={date}
                setDate={setDate}
                fetchLocations={fetchLocations}
                fetchAccountMembers={loadAccountMemberList}
                fetchForms={loadFormList}
                fetchProjects={loadProjectList}
                fetchResourcePlaces={loadResourcePlaces}
                fetchWorkFinishOptions={loadWorkFinishOptionList}
                isReady={isReady}
                isActive={activeTab === 0}
              />
            </TabPanel>
          </Collapse>
        </HeaderBox>
      </Box>
      <TabPanel value={0} sx={{ padding: 0 }}>
        <Box
          sx={{
            display: 'flex', flexDirection: 'column', backgroundColor: '#FFFFFF',
            minHeight: '50vh', alignItems: 'center', justifyContent: 'center'
          }} data-testid="map-content-box">
            {!isReady &&
              <Container
                sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}
                data-testid="loading-box">
                <CircularProgress />
              </Container>
            }
            {isError &&
            <Container sx={{ marginBottom: '16px' }}>
              <ErrorBox
                errorMessage="Ocorreu um problema ao buscar as informações do mapa. Clique novamente em 'Aplicar filtros' ou recarregue a página."
                onClose={() => setIsError(false)}
                title="Erro ao carregar os dados: Ocorreu um problema ao buscar as informações do mapa."
              />
            </Container>
            }
            {isReady && (
              (locations.works.length > 0 || locations.users.length > 0 || isDemoError || isDemo)
                ? <MapBox  ref={mapRef} locations={locations} />
                : <EmptyBox />
            )}
        </Box>
      </TabPanel>
      <TabPanel value={1} sx={{ padding: 0, flex: 1 }}>
        { isWorkTeamRoutingEnabled ? (
          <Box
          sx={{
            display: 'flex', flexDirection: 'column', backgroundColor: '#FFFFFF',
            minHeight: '50vh', alignItems: 'center', justifyContent: 'center'
          }} data-testid="route-content-box">
            <RoutesBox
              isReady={isRoutesReady}
              routes={routes}
              setRoutes={setRoutes}
              ref={routeMapRef}
              deleteRoute={deleteRoute}
              canCreateRoute={canCreateRoute}
              date={date}
            />
        </Box>
        ):(
          <Paywall></Paywall>
        )}
      </TabPanel>
    </TabContext>
  )
}

export default WorkUserMaps
